import React, { useState, useRef, useEffect } from 'react';
import './WallpaperGenerator.css';

const wallpaperSizes = [
  { label: 'iPhone SE', width: 640, height: 1136 },
  { label: 'iPhone 8', width: 750, height: 1334 },
  { label: 'iPhone 11', width: 828, height: 1792 },
  { label: 'iPhone 11 Pro', width: 1125, height: 2436 },
  { label: 'iPhone 11 Pro Max', width: 1242, height: 2688 },
  { label: 'iPhone 12', width: 1170, height: 2532 },
  { label: 'iPhone 12 Pro', width: 1170, height: 2532 },
  { label: 'iPhone 12 Pro Max', width: 1284, height: 2778 },
  { label: 'iPhone 13 Mini', width: 1080, height: 2340 },
  { label: 'iPhone 13', width: 1170, height: 2532 },
  { label: 'iPhone 13 Pro', width: 1170, height: 2532 },
  { label: 'iPhone 13 Pro Max', width: 1284, height: 2778 },
  { label: 'iPhone 14', width: 1284, height: 2778 },
  { label: 'iPhone 14 Plus', width: 1284, height: 2778 },
  { label: 'iPhone 14 Pro', width: 1290, height: 2796 },
  { label: 'iPhone 14 Pro Max', width: 1290, height: 2796 },
  { label: 'iPhone 15', width: 1179, height: 2556 },
  { label: 'iPhone 15 Plus', width: 1284, height: 2778 },
  { label: 'iPhone 15 Pro', width: 1200, height: 2556 },
  { label: 'iPhone 15 Pro Max', width: 1200, height: 2796 },
  { label: 'iPhone 16', width: 1200, height: 2796 },
  { label: 'iPhone 16 Plus', width: 1284, height: 2778 },
  { label: 'iPhone 16 Pro', width: 1290, height: 2796 },
  { label: 'iPhone 16 Pro Max', width: 1290, height: 2796 },
];

const defaultGoals = [
  {
    title: '健康管理', subtitle: '(保持身体健康)',
    tasks: ['每周锻炼3次', '每天喝足够的水', '每月进行一次健康检查'], color: '#4CAF50'
  },
  {
    title: '财务自由', subtitle: '(实现经济独立)',
    tasks: ['每月储蓄收入的20%', '投资于股票和加密货币', '学习财务管理知识'], color: '#2196F3'
  },
  {
    title: '新技术学习', subtitle: '(掌握前沿技术)',
    tasks: ['学习区块链开发', '掌握人工智能基础', '参与开源项目'], color: '#FFEB3B'
  },
  {
    title: '职业发展', subtitle: '(提升职业技能)',
    tasks: ['参加技术会议', '获得相关认证', '建立个人品牌'], color: '#F44336'
  },
];

const getClosestDeviceSize = (width: number) => {
  return wallpaperSizes.reduce((prev, curr) => {
    return (Math.abs(curr.width - width) < Math.abs(prev.width - width) ? curr : prev);
  });
};

const WallpaperGenerator: React.FC = () => {
  const [selectedSize, setSelectedSize] = useState(() => {
    const currentWidth = window.innerWidth;
    return getClosestDeviceSize(currentWidth);
  });
  const [wallpaper, setWallpaper] = useState<string | null>(null);
  const [goals, setGoals] = useState(defaultGoals);
  const [title, setTitle] = useState<string>('我的目标 2025');
  const [newGoal, setNewGoal] = useState({ title: '', subtitle: '', tasks: [''], color: '#000000' });
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Effect to generate wallpaper when title changes
  useEffect(() => {
    generateWallpaper(); // Call generateWallpaper whenever the title changes
  }, [title, goals, selectedSize]);

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setTitle(value); // Update title
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewGoal({ ...newGoal, [name]: value });
  };

  const handleTaskChange = (index: number, value: string) => {
    const updatedTasks = [...newGoal.tasks];
    updatedTasks[index] = value;
    setNewGoal({ ...newGoal, tasks: updatedTasks });
  };

  const addTask = (index: number) => {
    const updatedTasks = [...newGoal.tasks];
    updatedTasks.splice(index + 1, 0, '');
    setNewGoal({ ...newGoal, tasks: updatedTasks });
  };

  const delTask = (index: number) => {
    const updatedTasks = newGoal.tasks.filter((_, i) => i !== index);
    setNewGoal({ ...newGoal, tasks: updatedTasks });
  };

  const addGoalSection = () => {
    setGoals([...goals, newGoal]);
    setNewGoal({ title: '', subtitle: '', tasks: [''], color: '#000000' });
  };

  const deleteGoalSection = (index: number) => {
    const updatedGoals = goals.filter((_, i) => i !== index);
    setGoals(updatedGoals);
  };

  const getFontSize = (width: number) => {
    if (width <= 640) return 20;
    if (width <= 750) return 24;
    if (width <= 828) return 28;
    if (width <= 1125) return 32;
    if (width <= 1242) return 36;
    if (width <= 1170) return 28;
    if (width <= 1284) return 36;
    return 40;
  };

  const generateWallpaper = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {

        canvas.width = selectedSize.width;
        canvas.height = selectedSize.height;


        const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
        gradient.addColorStop(0, 'black');
        gradient.addColorStop(1, 'gray');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        const titleFontSize = getFontSize(selectedSize.width) + 15;
        const sectionFontSize = getFontSize(selectedSize.width);

        ctx.fillStyle = 'white';
        ctx.font = `bold ${titleFontSize}px Arial`;
        ctx.fillText(title, 50, (canvas.height / 2) - ((goals.length * 100) / 2) + 100);

        const startY = (canvas.height / 2) - ((goals.length * 100) / 2) + 200;
        const lineHeight = sectionFontSize + 20;
        const columnWidth = canvas.width / 2;
        let currentY = startY;

        goals.forEach((goal, index) => {
          let leftPad = 0;
          if (index % 2 == 0) {
            leftPad = 50;
          }
          const xOffset = (index % 2) * columnWidth;
          if (index % 2 === 0 && index !== 0) {
            currentY += lineHeight * (goal.tasks.length + 3) + 40;
          }
          ctx.font = `bold ${sectionFontSize}px Arial`;
          ctx.fillText(goal.title, xOffset + leftPad, currentY);
          ctx.fillStyle = goal.color;
          const titleWidth = ctx.measureText(goal.title).width;
          const rectX = xOffset + leftPad + titleWidth + 10;
          ctx.fillRect(rectX, currentY - titleFontSize / 2, titleFontSize / 3 * 2, titleFontSize / 3 * 2);
          ctx.fillStyle = 'white';
          ctx.font = `${sectionFontSize}px Arial`;
          ctx.fillText(goal.subtitle, xOffset + leftPad, currentY + lineHeight - 5);
          ctx.font = `${sectionFontSize}px Arial`;
          const downY = 20;
          goal.tasks.forEach((task, taskIndex) => {
            ctx.fillRect(xOffset + leftPad + 10, downY + currentY + 0.8 * lineHeight * (taskIndex + 1.5), 15, 15);
            ctx.fillText(task, xOffset + leftPad + 30, downY + currentY + 0.8 * lineHeight * (taskIndex + 2));
          });
        });
        const dataUrl = canvas.toDataURL('image/png');
        setWallpaper(dataUrl);
      }
    }
  };

  return (
    <div className="horizontal-layout">
      <div className="container">
        <div className="input-title">
          <select onChange={(e) => {
            setSelectedSize(wallpaperSizes[parseInt(e.target.value)]);
            generateWallpaper();
          }}>
            {wallpaperSizes.map((size, index) => (
              <option key={index} value={index}>
                {size.label}
              </option>
            ))}
          </select>
          <input
            type="text"
            name="title"
            placeholder="Goal Title"
            value={title}
            onChange={handleTitleChange}
          />
        </div>
        <div className="goal-section">
          <div className="input-row">
            <input
              type="text"
              name="title"
              placeholder="Goal Title"
              value={newGoal.title}
              onChange={handleInputChange}
            />
            <input
              type="text"
              name="subtitle"
              placeholder="Goal Subtitle"
              value={newGoal.subtitle}
              onChange={handleInputChange}
            />
            <input
              type="color"
              name="color"
              value={newGoal.color}
              onChange={handleInputChange}
              title="Select Square Color"
              className="color-picker"
            />
          </div>
          {newGoal.tasks.map((task, index) => (
            <div className="task-row" key={index}>
              <input
                type="text"
                placeholder={`Task ${index + 1}`}
                value={task}
                onChange={(e) => handleTaskChange(index, e.target.value)}
              />
              <button onClick={() => addTask(index)}>+</button>
              <button onClick={() => delTask(index)}>-</button>
            </div>
          ))}
          <button onClick={addGoalSection}>Add Goal Section</button>
        </div>
        <div className="added-goals">
          {goals.map((goal, index) => (
            <div key={index} className="goal-display">
              <div className="goal-title">
                {goal.title}
                <span className="goal-color" style={{ backgroundColor: goal.color }}></span>
              </div>
              <div className="goal-subtitle">{goal.subtitle}</div>
              <div className="goal-tasks">
                {goal.tasks.map((task, taskIndex) => (
                  <div key={taskIndex} className="task">
                    <span className="task-bullet"></span>
                    {task}
                  </div>
                ))}
              </div>
              <button onClick={() => deleteGoalSection(index)} style={{ marginLeft: '10px' }}>Delete</button>
            </div>
          ))}
        </div>
        <button onClick={generateWallpaper}>Generate Wallpaper</button>

      </div>
      <canvas ref={canvasRef} style={{ display: 'none' }}></canvas>
      <div className="image-display" style={{ maxWidth: '100%', maxHeight: '90vh', overflow: 'auto' }}>
        {wallpaper ? (
          <p><a href={wallpaper} download={`wallpaper-${selectedSize.label}.png`}>Download Wallpaper</a></p>
        ) : <p>Preview</p>}
        <section className={`iphone-section ${selectedSize.label.replace(/\s+/g, '-').toLowerCase()}`}>
          <div
            className="outside-border"
            style={{
              width: selectedSize.width,
              height: (selectedSize.height / selectedSize.width) * selectedSize.width,
              maxWidth: '90%',
              maxHeight: '90%',
            }}
          >
            <div className="silencer"></div>
            <div className="volume-up"></div>
            <div className="volume-down"></div>
            <div className="button-on"></div>
            <div className="inside-border">
              <div className="iphone-screen" style={{ backgroundImage: `url(${wallpaper})` }}>
              </div>
              {/* <!-- Camera --> */}
              <div className="camera">
                <div className="camera-dot">
                  <div className="camera-dot-2"></div>
                  <div className="camera-dot-3"></div>
                </div>
                <div className="camera-speaker">
                </div>
              </div>
              {/* <!-- Lock --> */}
              <div className="lock">
                <div className="lock-locked"></div>
              </div>
              {/* <!-- Time --> */}
              <div className="time">19:53</div>
              {/* <!-- Battery and Signal --> */}
              <div className="t-r-info">
                <div className="dots">...</div>
                <div className="battery">
                  <div className="bar"></div>
                  <div className="dot"></div>
                </div>
              </div>
              {/* <!-- Date --> */}
              <div className="date">Tuesday, 9 August</div>
              {/* <!-- Torch --> */}
              <div className="torch-outter">
                <div className="light"></div>
                <div className="top"></div>
                <div className="switch-top"></div>
                <div className="switch-section"></div>
                <div className="switch">
                  <div className="dot"></div>
                </div>
              </div>
              {/* <!-- Camera --> */}
              <div className="camera-outter">
                <div className="box"></div>
                <div className="eye"></div>
                <div className="circle"></div>
                <div className="dot"></div>
              </div>
              {/* <!-- Bottom Line --> */}
              <div className="bottom-line"></div>

            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default WallpaperGenerator;